import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { bannersApi } from '../services/api';

const FallbackHero = () => (
   <div className="relative h-96 md:h-[500px] lg:h-[600px] bg-gradient-to-br from-gold to-amber-600">
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="text-center">
        <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
          Welcome
        </h2>
        <p className="text-lg text-white/90 mb-8">Shop the latest collections</p>
        <Link 
          to="/shop-all"
           className="inline-block px-8 py-3 bg-white text-gold font-semibold rounded-lg hover:bg-gray-100 transition-colors"
        >
          Shop Now
        </Link>
      </div>
    </div>
  </div>
);

const HeroCarousel = () => {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchHeroBanners = async () => {
      try {
        const data = await bannersApi.getHeroBanners();
        setSlides(data || []);
      } catch (err) {
        console.error('Failed to fetch hero banners:', err);
        setSlides([]);
      } finally {
        setLoading(false);
      }
    };
    fetchHeroBanners();
  }, []);

  const [currentSlide, setCurrentSlide] = useState(0);
  const slidesLength = useRef(slides.length);

  useEffect(() => {
    slidesLength.current = slides.length;
  }, [slides]);

  useEffect(() => {
    if (slides.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slidesLength.current);
    }, 8000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const handleCtaClick = (link) => {
    if (link) {
      navigate(link);
    }
  };

  if (loading) return (
    <div className="w-full h-[500px] bg-gray-200 animate-pulse">
      <div className="h-full w-full flex items-center justify-center">
        <div className="space-y-4 text-center px-8">
          <div className="h-8 w-64 bg-gray-300 rounded mx-auto"></div>
          <div className="h-4 w-48 bg-gray-300 rounded mx-auto"></div>
          <div className="h-10 w-32 bg-gray-300 rounded mx-auto"></div>
        </div>
      </div>
    </div>
  );

  if (slides.length === 0) {
    return <FallbackHero />;
  }

  return (
    <div className="relative overflow-hidden">
      <div className="relative h-96 md:h-[500px] lg:h-[600px]">
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-700 ${index === currentSlide ? 'opacity-100' : 'opacity-0'}`}
          >
            {slide.image_url ? (
              <img
                src={slide.image_url}
                alt={slide.title || 'Hero banner'}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="absolute inset-0 bg-gradient-to-r from-pink-50 to-purple-50" />
            )}
            <div className="absolute inset-0 bg-black/30 flex items-center">
              <div className="max-w-7xl mx-auto px-4 w-full">
                <div className="max-w-xl">
                  <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
                    {slide.title || 'Welcome'}
                  </h2>
                  <p className="text-lg text-white/90 mb-8">{slide.subtitle || ''}</p>
                  {slide.link && (
                    <button 
                      onClick={() => handleCtaClick(slide.link)}
                      className="px-8 py-3 bg-accent hover:bg-accent-dark text-white font-semibold transition-colors"
                    >
                      Shop Now
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {slides.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/80 hover:bg-white flex items-center justify-center transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/80 hover:bg-white flex items-center justify-center transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-2 h-2 rounded-full transition-colors ${index === currentSlide ? 'bg-white' : 'bg-white/50'}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default HeroCarousel;